import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToHash } from "@/lib/scroll";
import { vimg } from "@/lib/api";

const LINES = ["PRECISION", "PEPTIDES.", "FOR ELITE RESEARCH."];
const EASE = [0.16, 1, 0.3, 1];

const MOLECULES = [
  { src: "/products/structures/ax-0157.png", cls: "w-[52vw] md:w-[40vw] -left-[8vw] top-[4%]", dur: 46 },
  { src: "/products/structures/r10.png", cls: "w-[44vw] md:w-[34vw] -right-[5vw] top-[26%]", dur: 58 },
  { src: "/products/structures/hgh-36.png", cls: "w-[36vw] md:w-[26vw] left-[32vw] bottom-[-10%]", dur: 52 },
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 550], [1, 0.1]);

  return (
    <section data-testid="hero-section" className="relative isolate min-h-screen flex flex-col justify-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0 overflow-hidden">
        {MOLECULES.map((m, i) => (
          <motion.img
            key={m.src}
            src={vimg(m.src)}
            alt=""
            aria-hidden
            initial={false}
            data-testid={`hero-molecule-${i}`}
            className={`absolute object-cover pointer-events-none select-none opacity-80 ${m.cls}`}
            style={{
              maskImage: "radial-gradient(closest-side, black 42%, transparent 74%)",
              WebkitMaskImage: "radial-gradient(closest-side, black 42%, transparent 74%)",
            }}
            animate={{ x: [0, 36, -24, 0], y: [0, -30, 20, 0], rotate: [0, 1.5, -1, 0] }}
            transition={{ duration: m.dur, repeat: Infinity, ease: "linear" }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-[#FBFBFC]/10 via-transparent to-[#FBFBFC]" />
      </motion.div>
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: "radial-gradient(640px circle at 28% 42%, rgba(109,40,217,0.09), transparent 62%)" }}
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-32 pb-24">
        <div className="overflow-hidden mb-8">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            data-testid="hero-overline"
            className="font-mono-clinical text-xs tracking-[0.3em] uppercase text-[#6D28D9]"
          >
            Aetheris Labs — Synthesis Division
          </motion.p>
        </div>

        <h1 data-testid="hero-heading" className="font-display font-black uppercase tracking-tighter leading-[0.92] text-[clamp(3rem,10vw,9rem)]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.15, delay: 0.25 + i * 0.16, ease: EASE }}
                className={`block ${i === 2 ? "text-[#A1A1AA]" : "text-[#09090B]"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            data-testid="hero-subcopy"
            className="text-base md:text-lg text-[#71717A] max-w-md leading-relaxed"
          >
            High-purity research peptides, synthesized under ISO-class cleanroom conditions and verified by
            third-party analytical labs. Batch COA with every vial.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="flex items-center gap-4"
          >
            <a
              href="#products"
              data-testid="hero-cta-explore"
              onClick={scrollToHash("#products")}
              className="px-8 py-4 bg-[#6D28D9] text-white font-mono-clinical text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#5B21B6] transition-colors duration-300"
            >
              Explore Compounds
            </a>
            <a
              href="#quality"
              data-testid="hero-cta-quality"
              onClick={scrollToHash("#quality")}
              className="px-8 py-4 border border-[#D4D4D8] text-[#09090B] font-mono-clinical text-xs tracking-[0.15em] uppercase hover:border-[#6D28D9] hover:text-[#6D28D9] transition-colors duration-300"
            >
              Our Standard
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#A1A1AA]"
      >
        <span className="font-mono-clinical text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
};
